/**
 * Что у нас звонит прямо сейчас — и чем это гасить.
 *
 * <p><b>Зачем ещё один реестр, когда есть {@link import('./activeCallRegistry')}.</b> Тот
 * отвечает на вопрос «телефон занят?» и заполняется экраном звонка. А звонящий входящий
 * экрана ещё не имеет: его рисует оверлей (Android, веб) или CallKit (iOS), и происходит
 * это из пуша, из WS-кадра или из фонового обработчика — в любом из которых React-дерева
 * может не быть вовсе. Отменить такой звонок по сигналу с сервера было нечем: снять
 * рингтон, шторку и системный экран умеет только тот, кто знает, что именно показано.
 *
 * <p>Отсюда и форма: модульная переменная, ключ — чат, значение — нога звонка, звонящий и
 * (на нативе) все UUID системного экрана, которые под этот звонок завели. UUID
 * <b>несколько</b> намеренно: один и тот же звонок доезжает двумя путями — VoIP-пушем и
 * WS-кадром, — и у них разные идентификаторы CallKit. Закрыть надо оба, иначе на телефоне
 * останется висеть фантомный «идёт звонок».
 *
 * <p>Запись живёт не дольше {@link RINGING_TTL_MS}: входящий, про который никто ничего не
 * сказал, всё равно перестаёт звонить по таймауту, а зависшая запись потом гасила бы
 * следующий звонок в том же чате.
 */

import { normCallChatId } from './callBusyRules';

export type RingingEntry = {
  chatId: string;
  inviteId: string | null;
  scope: 'direct' | 'group';
  callerUserId: string | null;
  /** UUID системного экрана звонка (CallKit / Telecom), если он заводился. */
  callUuids: string[];
  expiresAt: number;
};

/** Дольше минуты входящий не звонит ни в одной телефонной сети мира. */
const RINGING_TTL_MS = 90_000;

const byChat = new Map<string, RingingEntry>();

function sweep(now: number): void {
  for (const [key, entry] of byChat.entries()) {
    if (entry.expiresAt <= now) {
      byChat.delete(key);
    }
  }
}

/**
 * Отметить, что в чате звонит входящий.
 *
 * <p>Повторный вызов для того же чата не заводит вторую запись, а дополняет первую: тот же
 * звонок приезжает и пушем, и WS-кадром, и каждый приносит свой кусочек — один ногу,
 * другой UUID системного экрана.
 */
export function noteIncomingCallRinging(input: {
  chatId: string;
  inviteId?: string | null;
  scope?: 'direct' | 'group';
  callerUserId?: string | null;
  callUuid?: string | null;
}): void {
  const chatId = String(input.chatId ?? '');
  if (!chatId) {
    return;
  }
  const now = Date.now();
  sweep(now);
  const key = normCallChatId(chatId);
  const prev = byChat.get(key);
  const callUuids = prev ? [...prev.callUuids] : [];
  if (input.callUuid && !callUuids.includes(input.callUuid)) {
    callUuids.push(input.callUuid);
  }
  byChat.set(key, {
    chatId,
    // Ногу и звонящего не затираем пустым значением: кадр, приехавший вторым, часто беднее
    // первого (у пуша с холодного старта может не быть ни того, ни другого).
    inviteId: input.inviteId ?? prev?.inviteId ?? null,
    scope: input.scope ?? prev?.scope ?? 'direct',
    callerUserId: input.callerUserId ?? prev?.callerUserId ?? null,
    callUuids,
    expiresAt: now + RINGING_TTL_MS,
  });
}

/** Звонящий входящий в этом чате, если он есть. */
export function getRingingIncomingCall(chatId: string): RingingEntry | null {
  sweep(Date.now());
  return byChat.get(normCallChatId(chatId)) ?? null;
}

/** Все звонящие входящие — их может быть несколько, если звонят из разных чатов. */
export function listRingingIncomingCalls(): RingingEntry[] {
  sweep(Date.now());
  return [...byChat.values()];
}

/** Забыть входящий: он либо принят, либо отменён, либо отклонён. */
export function clearRingingIncomingCall(chatId: string): RingingEntry | null {
  const key = normCallChatId(chatId);
  const entry = byChat.get(key) ?? null;
  byChat.delete(key);
  return entry;
}

export function resetRingingIncomingCallsForTests(): void {
  byChat.clear();
}
